/**
 * Exports: CSV and CRA-friendly PDF from split snapshot.
 * Pure generators; storage and DB writes are done by the app layer.
 * @packageDocumentation
 */
import { TIPPO_PDF_PAGE_SIZE, TIPPO_PDF_GRID, TIPPO_PDF_SPACE, TIPPO_PDF_CARD_METRICS, TIPPO_PDF_TABLE_METRICS, TIPPO_PDF_TYPE, formatUsd, ellipsizeToWidth, clamp, createTippoPdfTheme, getCenteredBaselineY, } from "./tippoPdfKit";
/** Stable CSV column headers for tip allocation export. */
export const CSV_HEADERS = [
    "staffProfileId",
    "staffName",
    "roleId",
    "roleName",
    "amountCents",
    "periodStart",
    "periodEnd",
    "version",
    "computedAt",
];
/**
 * Generates CSV string from split snapshot. Uses stable columns; escapes quotes.
 *
 * @param snapshot - Snapshot from split_versions.snapshot_json
 * @returns CSV content (UTF-8)
 */
export function generateCsvFromSnapshot(snapshot) {
    const rows = [
        CSV_HEADERS.slice(),
        ...snapshot.payouts.map((p) => [
            escapeCsvField(p.staffProfileId),
            escapeCsvField(getStaffAllocationLabel(snapshot, p.staffProfileId)),
            escapeCsvField(p.roleId),
            escapeCsvField(getRoleAllocationLabel(snapshot, p.roleId)),
            String(p.amountCents),
            escapeCsvField(snapshot.periodStart),
            escapeCsvField(snapshot.periodEnd),
            String(snapshot.version),
            escapeCsvField(snapshot.computedAt),
        ]),
    ];
    return rows.map((r) => r.join(",")).join("\n");
}
/**
 * Escapes a CSV field: wrap in double quotes if contains comma, newline, or quote.
 */
function escapeCsvField(value) {
    const s = String(value);
    if (/[,"\n\r]/.test(s)) {
        return `"${s.replace(/"/g, '""')}"`;
    }
    return s;
}
function buildAllocationColumns(leftX, totalWidth) {
    const innerPad = 14;
    const usableWidth = totalWidth - innerPad * 2;
    const staffW = usableWidth * 0.42;
    const roleW = usableWidth * 0.38;
    const amtW = usableWidth * 0.2;
    const col0X = leftX + innerPad;
    const col1X = col0X + staffW;
    const col2X = col1X + roleW;
    return [
        { id: "staff", header: "Staff", x: col0X, width: staffW },
        { id: "role", header: "Role", x: col1X, width: roleW },
        { id: "amount", header: "Amount", x: col2X, width: amtW, align: "right" },
    ];
}
/** Row height for allocation rows (two-line cells when ID sublines are shown). */
const CRA_ALLOCATION_ROW_HEIGHT = TIPPO_PDF_TABLE_METRICS.rowHeight + TIPPO_PDF_TYPE.tiny.line + 4;
/** Human-readable staff label for CSV and PDF (IDs stay in separate columns/cells). */
function getStaffAllocationLabel(snapshot, staffProfileId) {
    return snapshot.staffNamesByProfileId?.[staffProfileId]?.trim() || "Staff (unknown)";
}
/** Human-readable role label for CSV and PDF. */
function getRoleAllocationLabel(snapshot, roleId) {
    const def = snapshot.roleDefinitions?.find((r) => r.id === roleId);
    const name = def?.name?.trim();
    if (name)
        return name;
    if (def)
        return "Unnamed role";
    return "Role (unknown)";
}
function resolveCraStaffCell(snapshot, staffProfileId) {
    const mapped = snapshot.staffNamesByProfileId?.[staffProfileId]?.trim();
    if (mapped)
        return { primary: mapped, secondary: null };
    return { primary: getStaffAllocationLabel(snapshot, staffProfileId), secondary: staffProfileId };
}
function resolveCraRoleCell(snapshot, roleId) {
    const def = snapshot.roleDefinitions?.find((r) => r.id === roleId);
    const name = def?.name?.trim();
    const primary = getRoleAllocationLabel(snapshot, roleId);
    if (name)
        return { primary, secondary: null };
    if (def)
        return { primary, secondary: roleId };
    return { primary, secondary: roleId };
}
function buildTipTypeColumns(leftX, totalWidth) {
    const innerPad = 14;
    const usableWidth = totalWidth - innerPad * 2;
    const labelW = usableWidth * 0.55;
    const amtW = usableWidth * 0.45;
    const col0X = leftX + innerPad;
    const col1X = col0X + labelW;
    return [
        { id: "label", header: "Category", x: col0X, width: labelW },
        { id: "amount", header: "Amount", x: col1X, width: amtW, align: "right" },
    ];
}
/** Soft-wrap long prose for PDF body lines (approximate char width at 10pt). */
function wrapTextToLines(text, maxChars) {
    const words = text.trim().split(/\s+/).filter(Boolean);
    const lines = [];
    let current = "";
    for (const w of words) {
        if (w.length > maxChars) {
            if (current) {
                lines.push(current);
                current = "";
            }
            lines.push(w);
            continue;
        }
        const next = current ? `${current} ${w}` : w;
        if (next.length > maxChars && current) {
            lines.push(current);
            current = w;
        }
        else {
            current = next;
        }
    }
    if (current)
        lines.push(current);
    return lines.length ? lines : [""];
}
/**
 * Generates a CRA-friendly PDF as Uint8Array. Uses the same Tippo visual system as
 * tip reports (palette, header band, tables, footer) when `logoPngBytes` is provided.
 *
 * @param context - CRA PDF context (restaurant, period, method, totals, summaries)
 * @param snapshot - Snapshot for payout lines
 * @param logoPngBytes - Optional square PNG logo (recommended for production)
 * @returns PDF bytes
 */
export async function generateCraPdf(context, snapshot, logoPngBytes) {
    const { PDFDocument, StandardFonts, rgb } = await import("pdf-lib");
    const doc = await PDFDocument.create();
    const font = await doc.embedFont(StandardFonts.Helvetica);
    const fontBold = await doc.embedFont(StandardFonts.HelveticaBold);
    const logo = logoPngBytes && logoPngBytes.length > 0
        ? await doc.embedPng(logoPngBytes)
        : null;
    const pageSize = TIPPO_PDF_PAGE_SIZE;
    const grid = TIPPO_PDF_GRID;
    const space = TIPPO_PDF_SPACE;
    const cardMetrics = TIPPO_PDF_CARD_METRICS;
    const tableMetrics = TIPPO_PDF_TABLE_METRICS;
    const type = TIPPO_PDF_TYPE;
    const theme = createTippoPdfTheme(rgb);
    const contentBottomY = grid.marginBottom + grid.footerHeight;
    const maxY = pageSize[1] - grid.marginTop;
    const contentLeftX = grid.marginX;
    const contentRightX = pageSize[0] - grid.marginX;
    const contentWidth = contentRightX - contentLeftX;
    const pages = [];
    let page = doc.addPage(pageSize);
    pages.push({ page });
    let y = maxY;
    const footerText = "Allocation statement produced by Tippo";
    const drawFooter = (p, pageIndex, pageCount) => {
        const logoSize = 16;
        const gap = 8;
        const textSize = 9;
        const textWidth = font.widthOfTextAtSize(footerText, textSize);
        const baseY = grid.marginBottom - 16;
        if (logo) {
            const groupWidth = logoSize + gap + textWidth;
            const startX = (pageSize[0] - groupWidth) / 2;
            p.drawImage(logo, { x: startX, y: baseY, width: logoSize, height: logoSize });
            p.drawText(footerText, {
                x: startX + logoSize + gap,
                y: baseY + 3,
                size: textSize,
                font,
                color: theme.mutedText,
            });
        }
        else {
            const startX = (pageSize[0] - textWidth) / 2;
            p.drawText(footerText, {
                x: startX,
                y: baseY + 3,
                size: textSize,
                font,
                color: theme.mutedText,
            });
        }
        const pageNumberText = `Page ${pageIndex + 1} of ${pageCount}`;
        const pageNumberSize = 9;
        const pageNumberWidth = font.widthOfTextAtSize(pageNumberText, pageNumberSize);
        p.drawText(pageNumberText, {
            x: contentRightX - pageNumberWidth,
            y: baseY + 3,
            size: pageNumberSize,
            font,
            color: theme.mutedText,
        });
    };
    const addPage = () => {
        page = doc.addPage(pageSize);
        y = maxY;
        pages.push({ page });
    };
    const ensureSpace = (needed) => {
        if (y - needed < contentBottomY)
            addPage();
    };
    const drawRule = (options) => {
        const lineY = options.y ?? y - 6;
        page.drawRectangle({
            x: contentLeftX,
            y: lineY,
            width: contentWidth,
            height: 1,
            color: options.color ?? theme.border,
        });
    };
    const drawSectionHeader = (title) => {
        ensureSpace(type.h2.line + space.lg + space.sm);
        y -= space.md;
        page.drawText(title, {
            x: contentLeftX,
            y,
            size: type.h2.size,
            font: fontBold,
            color: theme.accent,
        });
        y -= type.h2.line - 4;
        drawRule({ y: y + space.xs, color: theme.border });
        y -= space.md;
    };
    const drawBodyLine = (text, opts) => {
        const size = opts?.size ?? type.body.size;
        const line = clamp(size + 4, 10, 28);
        ensureSpace(line + 6);
        const f = opts?.bold ? fontBold : font;
        page.drawText(text, {
            x: contentLeftX,
            y,
            size,
            font: f,
            color: opts?.color ?? theme.text,
        });
        y -= line;
    };
    const drawHeaderBand = () => {
        const bandHeight = cardMetrics.headerBandHeight;
        ensureSpace(bandHeight + space.xl);
        page.drawRectangle({
            x: contentLeftX,
            y: y - bandHeight,
            width: contentWidth,
            height: bandHeight,
            color: theme.headerFill,
            borderColor: theme.border,
            borderWidth: 1,
        });
        const logoSize = 40;
        const padX = cardMetrics.paddingX;
        const padY = cardMetrics.paddingY;
        const bandTopY = y;
        const bandBottomY = y - bandHeight;
        let textLeft = contentLeftX + padX;
        if (logo) {
            const logoY = bandBottomY + (bandHeight - logoSize) / 2;
            page.drawImage(logo, {
                x: contentLeftX + padX,
                y: logoY,
                width: logoSize,
                height: logoSize,
            });
            textLeft = contentLeftX + padX + logoSize + space.lg;
        }
        const rightPad = cardMetrics.paddingX;
        const titleY = bandTopY - padY - 4;
        page.drawText("Tip allocation statement", {
            x: textLeft,
            y: titleY,
            size: type.title.size,
            font: fontBold,
            color: theme.text,
        });
        const metaSize = type.small.size;
        const metaY = titleY - space.sm - metaSize;
        const periodLine = `${context.periodStart} – ${context.periodEnd}`;
        const metaLeft = `${context.restaurantName} • ${periodLine}`;
        const metaLeftDraw = ellipsizeToWidth({
            text: metaLeft,
            maxWidth: contentRightX - rightPad - textLeft - 120,
            font,
            size: metaSize,
        });
        page.drawText(metaLeftDraw, {
            x: textLeft,
            y: metaY,
            size: metaSize,
            font,
            color: theme.mutedText,
        });
        const metaRight = `${context.versionStamp}`;
        const metaRightWidth = font.widthOfTextAtSize(metaRight, metaSize);
        page.drawText(metaRight, {
            x: contentRightX - rightPad - metaRightWidth,
            y: metaY,
            size: metaSize,
            font,
            color: theme.mutedText,
        });
        y = bandBottomY - space.xl;
    };
    const drawKeyValueRow = (options) => {
        const size = options.size ?? type.body.size;
        const line = options.rowHeight ?? clamp(size + 6, 12, 24);
        ensureSpace(line + 2);
        const baselineY = getCenteredBaselineY({ topY: y, rowHeight: line, fontSize: size });
        page.drawText(options.label, {
            x: options.labelX,
            y: baselineY,
            size,
            font: fontBold,
            color: theme.mutedText,
        });
        const valueWidth = font.widthOfTextAtSize(options.value, size);
        page.drawText(options.value, {
            x: options.valueRightX - valueWidth,
            y: baselineY,
            size,
            font: options.emphasizeValue ? fontBold : font,
            color: options.valueColor ?? theme.text,
        });
        y -= line;
    };
    const drawTable = (options) => {
        const headerHeight = tableMetrics.headerHeight;
        const rowHeight = options.getRowHeight();
        const drawHeader = () => {
            ensureSpace(headerHeight + 2);
            const topY = y;
            page.drawRectangle({
                x: contentLeftX,
                y: topY - headerHeight,
                width: contentWidth,
                height: headerHeight,
                color: theme.surfaceMuted,
                borderColor: theme.border,
                borderWidth: tableMetrics.strokeWidth,
            });
            page.drawRectangle({
                x: contentLeftX,
                y: topY - headerHeight,
                width: tableMetrics.strokeWidth,
                height: headerHeight,
                color: theme.border,
            });
            page.drawRectangle({
                x: contentRightX - tableMetrics.strokeWidth,
                y: topY - headerHeight,
                width: tableMetrics.strokeWidth,
                height: headerHeight,
                color: theme.border,
            });
            for (const col of options.columns) {
                const headerText = ellipsizeToWidth({
                    text: col.header,
                    maxWidth: col.width - 6,
                    font: fontBold,
                    size: type.small.size,
                });
                const headerWidth = fontBold.widthOfTextAtSize(headerText, type.small.size);
                const x = col.align === "right"
                    ? col.x + col.width - headerWidth
                    : col.x;
                const baselineY = getCenteredBaselineY({
                    topY,
                    rowHeight: headerHeight,
                    fontSize: type.small.size,
                });
                page.drawText(headerText, {
                    x,
                    y: baselineY,
                    size: type.small.size,
                    font: fontBold,
                    color: theme.mutedText,
                });
            }
            y -= headerHeight;
        };
        drawHeader();
        for (let i = 0; i < options.rows.length; i++) {
            ensureSpace(rowHeight + 2);
            if (y === maxY) {
                drawHeader();
                ensureSpace(rowHeight + 2);
            }
            const striped = i % 2 === 1;
            const topY = y;
            if (striped) {
                page.drawRectangle({
                    x: contentLeftX,
                    y: topY - rowHeight,
                    width: contentWidth,
                    height: rowHeight,
                    color: theme.stripeFill,
                });
            }
            page.drawRectangle({
                x: contentLeftX,
                y: topY - rowHeight,
                width: tableMetrics.strokeWidth,
                height: rowHeight,
                color: theme.border,
            });
            page.drawRectangle({
                x: contentRightX - tableMetrics.strokeWidth,
                y: topY - rowHeight,
                width: tableMetrics.strokeWidth,
                height: rowHeight,
                color: theme.border,
            });
            page.drawRectangle({
                x: contentLeftX,
                y: topY - rowHeight,
                width: contentWidth,
                height: tableMetrics.rowRuleWidth,
                color: theme.border,
            });
            options.drawRow(options.rows[i], i, striped, getCenteredBaselineY({
                topY,
                rowHeight,
                fontSize: type.small.size,
            }));
            y -= rowHeight;
        }
        y -= space.md;
    };
    drawHeaderBand();
    if (context.restaurantAddress) {
        ensureSpace(type.body.line + space.sm);
        drawBodyLine(`Address: ${context.restaurantAddress}`, {
            size: type.small.size,
            color: theme.mutedText,
        });
        y -= space.xs;
    }
    drawSectionHeader("Allocation method");
    for (const line of wrapTextToLines(context.methodStatement, 78)) {
        drawBodyLine(line, { size: type.body.size, color: theme.text });
    }
    y -= space.sm;
    if (context.tipTypeTotals) {
        drawSectionHeader("Totals by tip type");
        const tipCols = buildTipTypeColumns(contentLeftX, contentWidth);
        const colLabel = tipCols[0];
        const colAmt = tipCols[1];
        const tipRows = [
            { label: "Cash", cents: context.tipTypeTotals.cashCents },
            { label: "Controlled", cents: context.tipTypeTotals.controlledCents },
            { label: "Other", cents: context.tipTypeTotals.otherCents },
            { label: "Total", cents: context.totalTipCents },
        ];
        drawTable({
            columns: tipCols,
            rows: tipRows,
            getRowHeight: () => tableMetrics.rowHeight,
            drawRow: (row, _i, _s, baselineY) => {
                const r = row;
                const size = type.small.size;
                const isTotal = r.label === "Total";
                page.drawText(r.label, {
                    x: colLabel.x,
                    y: baselineY,
                    size,
                    font: isTotal ? fontBold : font,
                    color: theme.text,
                });
                const amt = formatUsd(r.cents);
                const amtWidth = font.widthOfTextAtSize(amt, size);
                page.drawText(amt, {
                    x: colAmt.x + colAmt.width - amtWidth,
                    y: baselineY,
                    size,
                    font: isTotal ? fontBold : font,
                    color: isTotal ? theme.accent : theme.text,
                });
            },
        });
    }
    else {
        drawSectionHeader("Total tips");
        const tipCols = buildTipTypeColumns(contentLeftX, contentWidth);
        const colLabel = tipCols[0];
        const colAmt = tipCols[1];
        drawTable({
            columns: tipCols,
            rows: [{ label: "Total tips (period)", cents: context.totalTipCents }],
            getRowHeight: () => tableMetrics.rowHeight,
            drawRow: (row, _i, _s, baselineY) => {
                const r = row;
                const size = type.small.size;
                page.drawText(r.label, {
                    x: colLabel.x,
                    y: baselineY,
                    size,
                    font: fontBold,
                    color: theme.text,
                });
                const amt = formatUsd(r.cents);
                const amtWidth = fontBold.widthOfTextAtSize(amt, size);
                page.drawText(amt, {
                    x: colAmt.x + colAmt.width - amtWidth,
                    y: baselineY,
                    size,
                    font: fontBold,
                    color: theme.accent,
                });
            },
        });
    }
    drawSectionHeader("Allocations");
    const allocCols = buildAllocationColumns(contentLeftX, contentWidth);
    const cStaff = allocCols[0];
    const cRole = allocCols[1];
    const cAmt = allocCols[2];
    drawTable({
        columns: allocCols,
        rows: snapshot.payouts.slice(),
        getRowHeight: () => CRA_ALLOCATION_ROW_HEIGHT,
        drawRow: (row, _i, _s, baselineY) => {
            const p = row;
            const size = type.small.size;
            const tiny = type.tiny.size;
            const rowTopY = baselineY + CRA_ALLOCATION_ROW_HEIGHT / 2 + size * 0.38;
            const staffCell = resolveCraStaffCell(snapshot, p.staffProfileId);
            const roleCell = resolveCraRoleCell(snapshot, p.roleId);
            const staffPrimaryY = staffCell.secondary ? rowTopY - 6 : baselineY;
            const staffSecondaryY = staffCell.secondary ? staffPrimaryY - tiny - 3 : null;
            const rolePrimaryY = roleCell.secondary ? rowTopY - 6 : baselineY;
            const roleSecondaryY = roleCell.secondary ? rolePrimaryY - tiny - 3 : null;
            const staffPrimaryText = ellipsizeToWidth({
                text: staffCell.primary,
                maxWidth: cStaff.width - 6,
                font,
                size,
            });
            page.drawText(staffPrimaryText, {
                x: cStaff.x,
                y: staffPrimaryY,
                size,
                font,
                color: theme.text,
            });
            if (staffCell.secondary && staffSecondaryY != null) {
                const sub = ellipsizeToWidth({
                    text: staffCell.secondary,
                    maxWidth: cStaff.width - 6,
                    font,
                    size: tiny,
                });
                page.drawText(sub, {
                    x: cStaff.x,
                    y: staffSecondaryY,
                    size: tiny,
                    font,
                    color: theme.mutedText,
                });
            }
            const rolePrimaryText = ellipsizeToWidth({
                text: roleCell.primary,
                maxWidth: cRole.width - 6,
                font,
                size,
            });
            page.drawText(rolePrimaryText, {
                x: cRole.x,
                y: rolePrimaryY,
                size,
                font,
                color: theme.text,
            });
            if (roleCell.secondary && roleSecondaryY != null) {
                const sub = ellipsizeToWidth({
                    text: roleCell.secondary,
                    maxWidth: cRole.width - 6,
                    font,
                    size: tiny,
                });
                page.drawText(sub, {
                    x: cRole.x,
                    y: roleSecondaryY,
                    size: tiny,
                    font,
                    color: theme.mutedText,
                });
            }
            const amt = formatUsd(p.amountCents);
            const amtWidth = font.widthOfTextAtSize(amt, size);
            page.drawText(amt, {
                x: cAmt.x + cAmt.width - amtWidth,
                y: baselineY,
                size,
                font: fontBold,
                color: theme.text,
            });
        },
    });
    drawSectionHeader("Overrides and disputes");
    const padX = cardMetrics.paddingX;
    const labelX = contentLeftX + padX;
    const valueRightX = contentRightX - padX;
    const kvCardHeight = cardMetrics.paddingY * 2 + cardMetrics.breakdownRowHeight * 2 + space.xs;
    ensureSpace(kvCardHeight + space.md);
    page.drawRectangle({
        x: contentLeftX,
        y: y - kvCardHeight,
        width: contentWidth,
        height: kvCardHeight,
        color: theme.surface,
        borderColor: theme.border,
        borderWidth: 1,
    });
    const cardTopY = y;
    y = cardTopY - cardMetrics.paddingY;
    drawKeyValueRow({
        label: "Overrides",
        value: context.overridesSummary,
        labelX,
        valueRightX,
        size: type.body.size,
        rowHeight: cardMetrics.breakdownRowHeight,
    });
    drawKeyValueRow({
        label: "Disputes",
        value: context.disputesSummary,
        labelX,
        valueRightX,
        size: type.body.size,
        rowHeight: cardMetrics.breakdownRowHeight,
    });
    y = cardTopY - kvCardHeight - space.lg;
    drawSectionHeader("Version");
    drawBodyLine(`Version stamp: ${context.versionStamp}`, { size: type.small.size });
    drawBodyLine(`Generated: ${context.generatedAt}`, {
        size: type.small.size,
        color: theme.mutedText,
    });
    const pageCount = pages.length;
    for (let i = 0; i < pages.length; i++) {
        drawFooter(pages[i].page, i, pageCount);
    }
    return doc.save();
}
